import React from 'react';
import { motion } from 'framer-motion';
import Press from './Press';
import Testimonials from './Testimonials';
import DestinationsMarquee from './DestinationsMarquee';
import CTA from './CTA';

export default function About() {
  return (
    <div className="bg-gradient-to-b from-[#395958] to-[#2a4342]">
      {/* Hero - Image Banner */}
      <section className="relative h-screen min-h-[600px] max-h-[900px] overflow-hidden pt-20 sm:pt-0">
        <img
          src="/pin7.jpg"
          alt="About YPD Weddings"
          className="absolute inset-0 w-full h-full object-cover scale-105 transition-transform duration-[15s] ease-out"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/70" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 h-full flex items-center justify-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="uppercase tracking-[0.4em] text-xs text-white/70 mb-8"
            >
              Our Story & Vision
            </motion.div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif text-white leading-tight mb-6 sm:mb-10">
              CRAFTING DREAMS INTO REALITY
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="text-sm sm:text-base md:text-lg text-white/90 max-w-4xl mx-auto leading-relaxed mb-8 sm:mb-12 px-4"
            >
              Founded on the belief that every love story deserves an extraordinary celebration, YPD Weddings transforms visions into unforgettable experiences. We are artisans of emotion, architects of joy, and guardians of your most precious moments.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="flex items-center justify-center"
            >
              <a href="/#contact" className="group relative px-8 sm:px-14 py-4 sm:py-6 border-2 border-white/80 text-white text-[10px] sm:text-[11px] tracking-[0.3em] sm:tracking-[0.4em] uppercase bg-transparent hover:bg-white hover:text-black transition-all duration-500 rounded-sm backdrop-blur-sm">
                <span className="pointer-events-none absolute -top-5 left-1/2 -translate-x-1/2 flex items-center gap-4 text-white group-hover:text-black transition-colors duration-300">
                  <span className="block h-px w-16 bg-white/60 group-hover:bg-black/60 transition-colors" />
                  <span className="block w-3 h-3 rounded-full border-2 border-white/70 group-hover:border-black/70 transition-colors" />
                  <span className="block h-px w-16 bg-white/60 group-hover:bg-black/60 transition-colors" />
                </span>
                DISCOVER OUR JOURNEY
                <div className="absolute inset-0 bg-white/5 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </a>
            </motion.div>
          </motion.div>
        </div>

        {/* Floating decorative elements */}
        <div className="absolute bottom-10 left-10 opacity-30">
          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
        </div>
        <div className="absolute top-20 right-20 opacity-20">
          <div className="w-1 h-1 bg-white rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
      </section>

      {/* Founder Story - Modern Card */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true, margin: '-100px' }}
            className="text-center mb-16"
          >
            <div className="uppercase tracking-[0.4em] text-xs text-[#395958] mb-6">Our Foundation</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-gray-900 mb-8 leading-tight">
              The Vision Behind<br />
              <span className="text-gray-700">YPD Weddings</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              viewport={{ once: true, margin: '-100px' }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-2xl shadow-[0_25px_50px_rgba(0,0,0,0.08)] border border-gray-200">
                <img
                  src="/pin13.jpg"
                  alt="Founder"
                  className="w-full h-64 sm:h-80 md:h-96 lg:h-[500px] object-cover transition-transform duration-700 hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/0 to-black/20" />

                {/* Decorative corner */}
                <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-gray-300" />
                <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-gray-300" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
              viewport={{ once: true, margin: '-100px' }}
              className="space-y-8"
            >
              <div>
                <h3 className="text-2xl md:text-3xl font-serif text-gray-900 mb-6 leading-tight">
                  From Dream to<br />
                  <span className="text-gray-700">Global Reality</span>
                </h3>
                <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-6">
                  What began as a passion for creating unforgettable moments has evolved into a worldwide celebration of love. Our founder's vision was simple yet profound: every couple deserves a wedding that reflects their unique story.
                </p>
                <p className="text-base text-gray-600 leading-relaxed">
                  Today, YPD Weddings stands as a testament to that vision, having orchestrated over 500 dream weddings across 50+ destinations worldwide, each one a masterpiece of emotion, artistry, and flawless execution.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:gap-6">
                <div className="p-4 sm:p-6 rounded-xl bg-gray-50 shadow-[0_20px_40px_rgba(0,0,0,0.1)] border border-gray-200">
                  <div className="text-xl sm:text-2xl font-serif text-gray-900 mb-2">15+</div>
                  <div className="text-xs sm:text-sm text-gray-600">Years of Excellence</div>
                </div>
                <div className="p-4 sm:p-6 rounded-xl bg-gray-50 shadow-[0_20px_40px_rgba(0,0,0,0.1)] border border-gray-200">
                  <div className="text-xl sm:text-2xl font-serif text-gray-900 mb-2">500+</div>
                  <div className="text-xs sm:text-sm text-gray-600">Dreams Realized</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values - Modern Cards */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            viewport={{ once: true, margin: '-100px' }}
            className="text-center mb-20"
          >
            <div className="uppercase tracking-[0.4em] text-xs text-[#395958] mb-6">Our Foundation</div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-gray-900 mb-8 leading-tight">
              Core Values That<br />
              <span className="text-gray-700">Define Us</span>
            </h2>
            <p className="text-base md:text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Every decision we make, every detail we perfect, and every moment we create is guided by these fundamental principles that have shaped our journey.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Artistry & Excellence',
                desc: 'We believe every wedding is a masterpiece waiting to be created, with no detail too small to perfect',
                img: '/pin2.jpg',
              },
              {
                title: 'Authentic Storytelling',
                desc: 'Your love story is unique, and we ensure every element reflects your personal journey and dreams',
                img: '/pin3.jpg',
              },
              {
                title: 'Global Perspective',
                desc: 'Drawing inspiration from cultures worldwide to create celebrations that transcend boundaries',
                img: '/pin4.jpg',
              },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: i * 0.1, ease: "easeOut" }}
                viewport={{ once: true, margin: '-100px' }}
                className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-gray-50 shadow-[0_25px_50px_rgba(0,0,0,0.1)] hover:shadow-[0_35px_70px_rgba(0,0,0,0.15)] transition-all duration-500"
              >
                <div className="relative h-48 sm:h-56 md:h-64 overflow-hidden">
                  <img
                    src={item.img}
                    alt={item.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/0 to-black/50 group-hover:from-black/20 group-hover:to-black/60 transition-all duration-500" />

                  {/* Floating icon */}
                  <div className="absolute top-4 sm:top-6 left-4 sm:left-6">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:bg-white transition-colors duration-300">
                      <div className="w-5 h-5 sm:w-6 sm:h-6 bg-black rounded-full"></div>
                    </div>
                  </div>
                </div>

                <div className="p-5 sm:p-6 md:p-8">
                  <h3 className="text-xl md:text-2xl font-serif text-gray-900 mb-4 leading-tight">{item.title}</h3>
                  <p className="text-sm md:text-base text-gray-700 leading-relaxed mb-6">{item.desc}</p>

                  <motion.a
                    href="/#about"
                    className="group/link inline-flex items-center gap-3 text-[#395958] text-[11px] tracking-[0.3em] uppercase hover:text-gray-900 transition-colors duration-300"
                    whileHover={{ x: 5 }}
                  >
                    <span>Learn More</span>
                    <svg className="w-4 h-4 transition-transform group-hover/link:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </motion.a>
                </div>

                {/* Decorative corner element */}
                <div className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Timeline - Modern Timeline */}
      <section className="py-20 bg-gradient-to-b from-[#395958] to-[#2a4342]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="uppercase tracking-[0.3em] text-xs text-[#F1D291]">Our Process</div>
            <h2 className="text-3xl md:text-4xl font-serif text-white mt-2">From Vision to Reality</h2>
            <p className="text-sm text-white/80 max-w-3xl mx-auto mt-4">
              Every extraordinary wedding begins with understanding your unique story. We guide you through a carefully crafted journey that transforms your dreams into an unforgettable celebration.
            </p>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute top-24 left-0 right-0 h-px bg-white/20" />
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { s: '01', t: 'Discovery', d: 'Understanding your vision, style, and dreams', img: '/discovery.jpg' },
                { s: '02', t: 'Design & Plan', d: 'Creating concepts, timelines, and detailed plans', img: '/design.jpg' },
                { s: '03', t: 'Curate & Coordinate', d: 'Selecting perfect partners and managing details', img: '/curate.jpg' },
                { s: '04', t: 'Execute & Celebrate', d: 'Flawless delivery of your dream wedding day', img: '/execute.jpg' },
              ].map((step, i) => (
                <motion.div
                  key={step.t}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-100px' }}
                  transition={{ duration: 0.6, delay: i * 0.05 }}
                  className="group relative overflow-hidden rounded-xl border border-white/20 bg-white/10 backdrop-blur-sm shadow-[0_20px_40px_rgba(0,0,0,0.3)]"
                >
                  <div className="absolute -top-4 left-6 z-10">
                    <div className="px-3 py-1 rounded-full bg-[#F1D291] text-[#2a4342] text-[10px] tracking-[0.3em]">{step.s}</div>
                  </div>
                  <div className="relative h-36 sm:h-40 md:h-44">
                    <img src={step.img} alt={step.t} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.05]" />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/0 to-black/40" />
                  </div>
                  <div className="p-4 sm:p-5 md:p-6">
                    <div className="text-lg font-serif text-white">{step.t}</div>
                    <p className="mt-2 text-sm text-white/80">{step.d}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials, Press, DestinationsMarquee for consistency with home */}
      <Testimonials />
      <Press />
      <DestinationsMarquee />
      <CTA />
    </div>
  );
}
